/**
 * BAIXAR O QUADRINHO SEM CONTA — T26.R2b (relatório de uso de 09/10/2026).
 *
 * O testador: «não consegui salvar em "Meus quadrinhos" (login bloqueado pela confirmação de
 * e-mail) nem exportar, então a HQ se perde ao fechar a aba»; e a primeira das três melhorias que
 * ele pediu: «Deixar o usuário sair com a HQ na mão: exportar PNG/PDF sem conta».
 *
 * Cada página do resultado (`.motor-moldura`, a folha já montada pela tela) vira uma imagem no
 * tamanho REAL da folha — a tela mostra a página encolhida, e baixar o encolhido daria um arquivo
 * borrado. O PDF tem uma página por página do quadrinho, do tamanho dela.
 */
import jsPDF from "jspdf";

/** O nome do arquivo: o título sem o que o sistema de arquivos recusa. */
export function nomeDoArquivo(titulo: string): string {
  const limpo = titulo
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/[^a-zA-Z0-9 _-]+/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .toLowerCase();
  return limpo.slice(0, 60) || "meu-quadrinho";
}

/** Devolve o controle ao navegador: a tela redesenha (o botão diz «Gerando…») e o clique responde. */
export const respirar = () => new Promise<void>((r) => setTimeout(r, 0));

/**
 * O pedaço da imagem que `object-fit: cover` mostra numa caixa `w × h` — a mesma conta do
 * navegador, para o arquivo baixado ter o mesmo recorte da tela.
 */
export function recorteCover(nw: number, nh: number, w: number, h: number): { sx: number; sy: number; sw: number; sh: number } {
  if (!nw || !nh || !w || !h) return { sx: 0, sy: 0, sw: nw, sh: nh };
  const escala = Math.max(w / nw, h / nh);
  const sw = w / escala;
  const sh = h / escala;
  return { sx: (nw - sw) / 2, sy: (nh - sh) / 2, sw, sh };
}

/** «12px», «50%» → pixels; o raio de canto que o navegador já resolveu no estilo calculado. */
function raio(valor: string, lado: number): number {
  const v = parseFloat(valor) || 0;
  return valor.trim().endsWith("%") ? (v / 100) * lado : v;
}
function raiosDoCanto(st: CSSStyleDeclaration, w: number, h: number): DOMPointInit[] {
  return [st.borderTopLeftRadius, st.borderTopRightRadius, st.borderBottomRightRadius, st.borderBottomLeftRadius].map((c) => {
    const [x, y = x] = c.split(/\s+/);
    return { x: Math.min(raio(x, w), w / 2), y: Math.min(raio(y, h), h / 2) };
  });
}
const transparente = (cor: string) => !cor || cor === "transparent" || /rgba\([^)]*,\s*0\)$/.test(cor);

/**
 * T29.1 · A PÁGINA DESENHADA À MÃO NUM `<canvas>`, sem o html2canvas.
 *
 * O relatório de 14/10: «"Baixar em PDF" congela o navegador por 44 segundos sem sinal». Medido
 * aqui em 20/10 com 3 páginas e 9 desenhos de 1024 px, com o processador 8× mais lento (um
 * computador de escola): o html2canvas levou 13 s e a tela ficou parada até 4,6 s seguidos — ele
 * copia o documento INTEIRO a cada página, e 8 dos 13 s eram a cópia, mesmo sem as imagens.
 *
 * A folha já está desenhada na tela; o navegador já sabe onde está cada coisa. Então cada
 * elemento é copiado da tela para o canvas pela posição que ele tem nela: o desenho, já
 * decodificado, com o recorte do `object-fit: cover`; o quadro e o balão com o fundo, a borda e
 * o canto do estilo calculado; e o texto palavra por palavra, na posição em que o navegador o
 * quebrou — por isso o arquivo tem as mesmas linhas da tela.
 *
 * O que este caminho não sabe desenhar (motor SVG, retícula, elemento girado) devolve `null`, e
 * aquela página vai pelo html2canvas, como antes.
 */
export async function desenharAPagina(moldura: HTMLElement, larguraReal: number): Promise<HTMLCanvasElement | null> {
  const base = moldura.getBoundingClientRect();
  if (!base.width) return null;
  const k = larguraReal / base.width;
  const todos = [moldura, ...Array.from(moldura.querySelectorAll<HTMLElement>("*"))];
  if (todos.some((e) => e instanceof SVGElement || e.classList.contains("motor-reticula") || /rotate|matrix/.test(e.style.transform))) return null;

  const tela = document.createElement("canvas");
  tela.width = Math.round(base.width * k);
  tela.height = Math.round(base.height * k);
  const g = tela.getContext("2d");
  if (!g) return null;
  g.fillStyle = "#ffffff";
  g.fillRect(0, 0, tela.width, tela.height);

  const caixa = (r: DOMRect) => ({ x: (r.left - base.left) * k, y: (r.top - base.top) * k, w: r.width * k, h: r.height * k });

  for (const el of todos) {
    const st = getComputedStyle(el);
    if (st.display === "none" || st.visibility === "hidden") continue;
    const r = el.getBoundingClientRect();
    if (!r.width || !r.height) continue;
    const c = caixa(r);
    // Fonte, borda e canto do estilo calculado estão em pixels da FOLHA, antes da escala da tela
    // (`transform: scale`); o retângulo já está em pixels da tela. `e` leva um ao outro.
    const e = (el.offsetWidth ? r.width / el.offsetWidth : 1) * k;

    if (el instanceof HTMLImageElement) {
      if (!el.complete || !el.naturalWidth) await el.decode().catch(() => undefined);
      if (!el.naturalWidth) continue;
      const corte = recorteCover(el.naturalWidth, el.naturalHeight, c.w, c.h);
      g.drawImage(el, corte.sx, corte.sy, corte.sw, corte.sh, c.x, c.y, c.w, c.h);
      continue;
    }

    const fundo = st.backgroundColor;
    const borda = parseFloat(st.borderTopWidth) * e || 0;
    const corDaBorda = st.borderTopColor;
    if (!transparente(fundo) || (borda && st.borderTopStyle !== "none")) {
      const cantos = raiosDoCanto(st, el.offsetWidth || r.width, el.offsetHeight || r.height).map((p) => ({ x: (p.x ?? 0) * e, y: (p.y ?? 0) * e }));
      if (!transparente(fundo)) {
        g.beginPath();
        g.roundRect(c.x, c.y, c.w, c.h, cantos);
        g.fillStyle = fundo;
        g.fill();
      }
      if (borda && st.borderTopStyle !== "none" && !transparente(corDaBorda)) {
        // A borda do CSS fica DENTRO da caixa; o traço do canvas é centrado na linha.
        g.beginPath();
        g.roundRect(c.x + borda / 2, c.y + borda / 2, c.w - borda, c.h - borda, cantos.map((p) => ({ x: Math.max(0, p.x - borda / 2), y: Math.max(0, p.y - borda / 2) })));
        g.lineWidth = borda;
        g.strokeStyle = corDaBorda;
        g.setLineDash(st.borderTopStyle === "dashed" ? [borda * 3, borda * 2] : []);
        g.stroke();
        g.setLineDash([]);
      }
    }

    // O texto que é DESTE elemento (os nós de texto filhos diretos), palavra por palavra.
    for (const no of Array.from(el.childNodes)) {
      if (no.nodeType !== Node.TEXT_NODE || !no.textContent?.trim()) continue;
      g.font = `${st.fontStyle} ${st.fontWeight} ${parseFloat(st.fontSize) * e}px ${st.fontFamily}`;
      g.fillStyle = st.color;
      g.textBaseline = "alphabetic";
      const texto = no.textContent;
      const faixa = document.createRange();
      const palavra = /\S+/g;
      let m: RegExpExecArray | null;
      while ((m = palavra.exec(texto))) {
        // Uma palavra quebrada por `overflow-wrap: anywhere` tem mais de um retângulo: cada
        // pedaço vai na linha em que o navegador o pôs.
        let inicio = m.index;
        for (let i = m.index; i < m.index + m[0].length; i++) {
          faixa.setStart(no, inicio);
          faixa.setEnd(no, i + 1);
          if (faixa.getClientRects().length > 1) {
            desenharPedaco(g, faixa, no, inicio, i, base, k);
            inicio = i;
          }
        }
        desenharPedaco(g, faixa, no, inicio, m.index + m[0].length, base, k);
      }
    }
  }
  return tela;
}

function desenharPedaco(g: CanvasRenderingContext2D, faixa: Range, no: Node, de: number, ate: number, base: DOMRect, k: number) {
  if (ate <= de) return;
  faixa.setStart(no, de);
  faixa.setEnd(no, ate);
  const r = faixa.getBoundingClientRect();
  if (!r.width) return;
  const medida = g.measureText((no.textContent ?? "").slice(de, ate));
  const sobe = medida.fontBoundingBoxAscent ?? medida.actualBoundingBoxAscent;
  const desce = medida.fontBoundingBoxDescent ?? medida.actualBoundingBoxDescent;
  // O retângulo da faixa tem a altura da fonte (subida + descida); a linha de base fica onde a
  // subida termina, na proporção da fonte.
  const y = (r.top - base.top) * k + (r.height * k * sobe) / (sobe + desce || 1);
  g.fillText((no.textContent ?? "").slice(de, ate), (r.left - base.left) * k, y);
}

/**
 * As páginas do resultado como imagens no tamanho real da folha (`larguras[i]` em pixels).
 *
 * T29.1 · Entre uma página e outra o controle volta ao navegador, e `aoAvancar` conta em qual
 * está — a tela mostra «Montando a página 2 de 3…» em vez de parar sem sinal.
 */
export async function paginasComoImagens(
  raiz: HTMLElement,
  larguras: number[],
  aoAvancar?: (pagina: number, total: number) => void
): Promise<HTMLCanvasElement[]> {
  const molduras = Array.from(raiz.querySelectorAll<HTMLElement>(".motor-moldura"));
  const telas: HTMLCanvasElement[] = [];
  for (let i = 0; i < molduras.length; i++) {
    aoAvancar?.(i + 1, molduras.length);
    await respirar();
    const m = molduras[i];
    const naTela = m.getBoundingClientRect().width || m.offsetWidth || 1;
    const real = larguras[i] ?? naTela;
    const desenhada = await desenharAPagina(m, real).catch(() => null);
    if (desenhada) {
      telas.push(desenhada);
      continue;
    }
    const escala = Math.min(3, Math.max(1, real / naTela));
    const { default: html2canvas } = await import("html2canvas");
    telas.push(await html2canvas(m, { scale: escala, backgroundColor: "#ffffff", useCORS: true, logging: false }));
  }
  return telas;
}

function baixar(url: string, nome: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = nome;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
}

/** Um PDF com uma página por página do quadrinho, cada uma do tamanho dela. */
export async function baixarPDF(titulo: string, telas: HTMLCanvasElement[]) {
  if (!telas.length) return;
  const primeira = telas[0];
  const pdf = new jsPDF({ unit: "px", format: [primeira.width, primeira.height], orientation: primeira.height >= primeira.width ? "p" : "l", hotfixes: ["px_scaling"] });
  for (let i = 0; i < telas.length; i++) {
    const t = telas[i];
    if (i > 0) pdf.addPage([t.width, t.height], t.height >= t.width ? "p" : "l");
    pdf.addImage(t.toDataURL("image/jpeg", 0.92), "JPEG", 0, 0, t.width, t.height);
    await respirar();
  }
  const url = URL.createObjectURL(pdf.output("blob"));
  baixar(url, `${nomeDoArquivo(titulo)}.pdf`);
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}

/** Uma imagem PNG por página. */
export async function baixarPNGs(titulo: string, telas: HTMLCanvasElement[]) {
  for (let i = 0; i < telas.length; i++) {
    const blob: Blob | null = await new Promise((r) => telas[i].toBlob(r, "image/png"));
    if (!blob) continue;
    const url = URL.createObjectURL(blob);
    baixar(url, `${nomeDoArquivo(titulo)}${telas.length > 1 ? `-pagina-${i + 1}` : ""}.png`);
    setTimeout(() => URL.revokeObjectURL(url), 10_000);
    // Navegadores seguram downloads em rajada; um respiro entre um e outro.
    await new Promise((r) => setTimeout(r, 300));
  }
}
